import { z } from 'zod'
import sortBy from 'lodash/sortBy'
import { type BuyOverDiscountTier } from 'components/Pricing/types/discount.types'
import { type GoalFacts } from './discountSteps'

// Raw wire shape of a BUYOVER discount, shared by the /discounts and
// checkout-summary boundaries.

const RawBuyoverTierSchema = z.object({
  buyOverPrice: z.number(),
  buyOverDiscountPercentage: z.number(),
})

export const RawBuyoverDataSchema = z.object({
  buyOverPrice: z.number(),
  buyOverDiscountPercentage: z.number(),
  tiers: z.array(RawBuyoverTierSchema).optional(),
})

export const RawBuyoverRedemptionSchema = z.object({
  applied: z.boolean(),
  // Amount missing to the NEXT tier — sent even when a lower tier is applied.
  missingAmountForDiscount: z.number().optional(),
  appliedTierIndex: z.number().nullable().optional(),
})

export type RawBuyoverTier = z.infer<typeof RawBuyoverTierSchema>
export type RawBuyoverData = z.infer<typeof RawBuyoverDataSchema>
export type RawBuyoverRedemption = z.infer<typeof RawBuyoverRedemptionSchema>

export type BuyoverTier = {
  price: number
  discountPercent: number
  applied: boolean
  remaining: number
}

// The ladder, ascending by threshold — a discount without `tiers` is a single
// tier made of the flat fields. Sorting here is what lets every caller treat
// the last tier as the biggest discount.
export function extractRawTiers(buyOverData: RawBuyoverData): RawBuyoverTier[] {
  const tiers = buyOverData.tiers?.length
    ? buyOverData.tiers
    : [
        {
          buyOverPrice: buyOverData.buyOverPrice,
          buyOverDiscountPercentage: buyOverData.buyOverDiscountPercentage,
        },
      ]
  return sortBy(tiers, 'buyOverPrice')
}

// The backend owns `applied` and `missingAmountForDiscount` — they account for
// free-item credits the raw product total doesn't reflect. `productTotal` only
// backs the legacy single-tier shape, which may omit the missing amount.
export function normalizeBuyoverTiers({
  buyOverData,
  discountRedemption,
  productTotal,
}: {
  buyOverData: RawBuyoverData
  discountRedemption: RawBuyoverRedemption
  productTotal?: number
}): BuyoverTier[] {
  const rawTiers = extractRawTiers(buyOverData)
  const appliedIndex = !discountRedemption.applied
    ? -1
    : // The legacy single-tier shape omits the index and can only mean its one tier.
      discountRedemption.appliedTierIndex ?? 0

  const firstUnapplied = rawTiers[appliedIndex + 1]
  const missingToFirstUnapplied = firstUnapplied
    ? discountRedemption.missingAmountForDiscount ??
      Math.max(firstUnapplied.buyOverPrice - (productTotal ?? 0), 0)
    : 0

  return rawTiers.map((tier, index) => ({
    price: tier.buyOverPrice,
    discountPercent: tier.buyOverDiscountPercentage,
    applied: index <= appliedIndex,
    remaining:
      index <= appliedIndex
        ? 0
        : // The backend only reports the missing amount to the next tier —
          // extend it by the price gap for the ones past it.
          missingToFirstUnapplied +
          (tier.buyOverPrice - firstUnapplied.buyOverPrice),
  }))
}

// Every goal carries the order-wide `totalSaved`, but only the reached tier's
// copy reads it.
export function buyoverTiersToGoals(
  tiers: BuyoverTier[],
  totalSaved: number
): Exclude<GoalFacts, { goal: 'shipping' }>[] {
  return tiers.map((tier, index) => {
    const isHighestTier = index === tiers.length - 1
    // A $0-threshold tier always applies, so communicate it as a flat percent.
    return tier.price === 0
      ? {
          goal: 'percent' as const,
          applied: true as const,
          percentOff: tier.discountPercent,
          isHighestTier,
        }
      : {
          goal: 'buyover' as const,
          target: tier.price,
          remaining: tier.remaining,
          applied: tier.applied,
          discountPercent: tier.discountPercent,
          totalSaved,
          isHighestTier,
        }
  })
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string

// "25% off $130+ • 50% off $180+". Price formatting differs per surface, so the
// caller supplies it.
export function getBuyoverTiersText(
  tiers: BuyOverDiscountTier[],
  t: TranslateFn,
  formatTierPrice: (price: number) => string
): string {
  return tiers
    .map((tier) =>
      t('order.checkout.payment_summary_rows.buy_over_tier', {
        v1: tier.buyOverDiscount,
        v2: formatTierPrice(tier.buyOverPrice),
      })
    )
    .reduce((joined, tierText) =>
      t('order.checkout.payment_summary_rows.buy_over_tiers_pair', {
        v1: joined,
        v2: tierText,
      })
    )
}
